import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, Users, Clock, Loader, Banknote, CheckCircle, XCircle, CreditCard, Edit3, X, User, Mail, Phone, FileText, Send } from 'lucide-react';
import { bookingService, apartmentService } from '../../services/api';
import toast from 'react-hot-toast';

interface Booking {
  id: number;
  code_reservation: string;
  date_debut: string;
  date_fin: string;
  nb_personnes: number;
  nb_nuits: number;
  montant_total: number;
  status: string;
  payment_status: string;
  mode_paiement: string | null;
  notes?: string;
  is_deposit?: boolean;
  deposit_amount?: number;
  deposit_paid?: boolean;
  remaining_amount?: number;
  apartment?: {
    id: number;
    titre: string;
    ville: string;
  };
  locataire?: {
    id: number;
    nom: string;
    prenom: string;
    email: string;
    telephone?: string;
  };
  guest?: {
    name: string;
    email: string;
    phone: string;
  };
}

interface Apartment {
  id: number;
  titre: string;
}

const Bookings: React.FC = () => {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [apartments, setApartments] = useState<Apartment[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');
  const [confirming, setConfirming] = useState<number | null>(null);

  // Modal de modification
  const [showModal, setShowModal] = useState(false);
  const [editingBooking, setEditingBooking] = useState<Booking | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [editForm, setEditForm] = useState({
    apartment_id: 0,
    date_debut: '',
    date_fin: '',
    guest_name: '',
    guest_email: '',
    guest_phone: '',
    nb_personnes: 1,
    montant_total: 0,
    notes: '',
    status: 'confirmed' as 'confirmed' | 'pending' | 'completed'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [bookingsRes, apartmentsRes] = await Promise.all([
        bookingService.getGerantBookings(),
        apartmentService.getMyApartments()
      ]);
      setBookings(bookingsRes.data.bookings || []);
      setApartments(apartmentsRes.data.apartments || []);
    } catch (error) {
      console.error(error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const fetchBookings = async () => {
    try {
      const response = await bookingService.getGerantBookings();
      setBookings(response.data.bookings || []);
    } catch (error) {
      console.error(error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  // Ouvrir le modal de modification
  const openEditModal = (booking: Booking) => {
    setEditingBooking(booking);
    
    // Récupérer les infos du client
    let guestName = '';
    let guestEmail = '';
    let guestPhone = '';
    
    if (booking.locataire) {
      guestName = `${booking.locataire.prenom} ${booking.locataire.nom}`;
      guestEmail = booking.locataire.email || '';
      guestPhone = booking.locataire.telephone || '';
    } else if (booking.guest) {
      guestName = booking.guest.name || '';
      guestEmail = booking.guest.email || '';
      guestPhone = booking.guest.phone || '';
    }
    
    setEditForm({
      apartment_id: booking.apartment?.id || 0,
      date_debut: booking.date_debut.split('T')[0],
      date_fin: booking.date_fin.split('T')[0],
      guest_name: guestName,
      guest_email: guestEmail,
      guest_phone: guestPhone,
      nb_personnes: booking.nb_personnes || 1,
      montant_total: booking.montant_total || 0,
      notes: booking.notes || '',
      status: booking.status as 'confirmed' | 'pending' | 'completed'
    });
    setShowModal(true);
  };

  // Soumettre la modification
  const handleSubmitEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingBooking) return;
    
    if (!editForm.guest_name || !editForm.guest_phone) {
      toast.error('Veuillez remplir le nom et téléphone du client');
      return;
    }
    
    if (new Date(editForm.date_fin) <= new Date(editForm.date_debut)) {
      toast.error('La date de fin doit être après la date de début');
      return;
    }
    
    setSubmitting(true);
    try {
      await bookingService.updateGerant(editingBooking.id, {
        apartment_id: editForm.apartment_id,
        date_debut: editForm.date_debut,
        date_fin: editForm.date_fin,
        guest_name: editForm.guest_name,
        guest_email: editForm.guest_email || undefined,
        guest_phone: editForm.guest_phone,
        nb_personnes: editForm.nb_personnes,
        montant_total: editForm.montant_total,
        notes: editForm.notes || undefined,
        status: editForm.status
      });
      
      toast.success('Réservation modifiée avec succès !');
      setShowModal(false);
      setEditingBooking(null);
      fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erreur lors de la modification');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmCash = async (bookingId: number) => {
    if (!window.confirm('Confirmez-vous avoir reçu le paiement en espèces ?')) return;
    
    setConfirming(bookingId);
    try {
      await bookingService.confirmCashPayment(bookingId);
      toast.success('Paiement validé !');
      fetchBookings();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erreur lors de la validation');
    } finally {
      setConfirming(null);
    }
  };

  const handleConfirmDeposit = async (bookingId: number, depositAmount: number) => {
    if (!window.confirm(`Confirmez-vous avoir reçu l'acompte de ${formatPrice(depositAmount)} FCFA ?`)) return;
    
    setConfirming(bookingId);
    try {
      await bookingService.confirmDepositPayment(bookingId);
      toast.success('Acompte validé ! La facture est disponible.');
      fetchBookings();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erreur lors de la validation');
    } finally {
      setConfirming(null);
    }
  };

  const handleDownloadInvoice = async (bookingId: number, code: string) => {
    try {
      const response = await bookingService.downloadInvoice(bookingId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `facture_${code}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erreur lors du téléchargement');
    }
  };

  const handleCancel = async (bookingId: number) => {
    if (!window.confirm('Annuler cette réservation ?')) return;
    
    try {
      await bookingService.cancel(bookingId);
      toast.success('Réservation annulée');
      fetchBookings();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  const formatPrice = (price: number) => new Intl.NumberFormat('fr-FR').format(price);
  const formatDate = (date: string) => new Date(date).toLocaleDateString('fr-FR', { 
    day: 'numeric', month: 'short', year: 'numeric' 
  });

  const getStatusBadge = (booking: Booking) => {
    if (booking.status === 'pending') {
      if (booking.is_deposit) {
        return <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">💰 Acompte à valider</span>;
      }
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">💵 À valider</span>;
    }
    if (booking.status === 'confirmed' && booking.is_deposit && booking.deposit_paid) {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">🔵 Acompte payé</span>;
    }
    const badges: Record<string, { label: string; className: string }> = {
      confirmed: { label: 'Confirmée', className: 'bg-green-100 text-green-800' },
      cancelled: { label: 'Annulée', className: 'bg-red-100 text-red-800' },
      completed: { label: 'Terminée', className: 'bg-neutral-100 text-neutral-800' },
    };
    const badge = badges[booking.status] || { label: booking.status, className: 'bg-neutral-100' };
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.className}`}>{badge.label}</span>;
  };

  const getClientName = (booking: Booking) => {
    if (booking.locataire) {
      return `${booking.locataire.prenom} ${booking.locataire.nom}`;
    }
    if (booking.guest) {
      return `${booking.guest.name} (Guest)`;
    }
    return 'N/A';
  };

  const getClientContact = (booking: Booking) => {
    if (booking.locataire) return booking.locataire.email;
    if (booking.guest) return booking.guest.email;
    return '';
  };

  const filteredBookings = bookings.filter(b => {
    if (filter === 'all') return true;
    if (filter === 'pending_validation') return b.status === 'pending';
    return b.status === filter;
  });

  // Compter les réservations en attente de validation
  const pendingCount = bookings.filter(b => b.status === 'pending').length;

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold">Réservations</h1>
          <p className="text-neutral-600">Gérez les réservations de vos appartements</p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/gerant/bookings/invitation/new"
            className="btn btn-primary flex items-center gap-2"
          >
            <Send className="w-4 h-4" />
            Créer une invitation
          </Link>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="input w-auto"
          >
            <option value="all">Toutes ({bookings.length})</option>
            {pendingCount > 0 && (
              <option value="pending_validation">💵 À valider ({pendingCount})</option>
            )}
            <option value="confirmed">Confirmées</option>
            <option value="completed">Terminées</option>
            <option value="cancelled">Annulées</option>
          </select>
        </div>
      </div>

      {/* Alerte paiements en attente */}
      {pendingCount > 0 && filter !== 'pending_validation' && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-center gap-3">
          <Banknote className="w-6 h-6 text-orange-500" />
          <div className="flex-1">
            <p className="font-medium text-orange-800">
              {pendingCount} réservation{pendingCount > 1 ? 's' : ''} en attente de validation
            </p>
            <p className="text-orange-700 text-sm">Confirmez la réception des paiements pour valider</p>
          </div>
          <button 
            onClick={() => setFilter('pending_validation')}
            className="btn btn-sm bg-orange-500 text-white hover:bg-orange-600"
          >
            Voir
          </button>
        </div>
      )}

      {filteredBookings.length === 0 ? (
        <div className="card p-8 text-center">
          <Calendar className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
          <p className="text-neutral-500">
            {filter === 'all' ? 'Aucune réservation pour le moment.' : 'Aucune réservation avec ce filtre.'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredBookings.map((booking) => (
            <div key={booking.id} className={`card p-4 sm:p-6 ${
              booking.status === 'pending' 
                ? 'border-2 border-orange-300 bg-orange-50/30' 
                : ''
            }`}>
              <div className="flex flex-col lg:flex-row justify-between gap-4">
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-3 mb-2">
                    <span className="font-mono text-sm bg-neutral-100 px-2 py-1 rounded">
                      {booking.code_reservation}
                    </span>
                    {getStatusBadge(booking)}
                    {booking.mode_paiement && (
                      <span className="text-xs text-neutral-500 flex items-center gap-1">
                        {booking.mode_paiement === 'cash' ? <Banknote className="w-3 h-3" /> : <CreditCard className="w-3 h-3" />}
                        {booking.mode_paiement === 'cash' ? 'Espèces' : booking.mode_paiement === 'wave' ? 'Wave' : booking.mode_paiement === 'paypal' ? 'PayPal' : booking.mode_paiement}
                      </span>
                    )}
                  </div>
                  
                  {booking.apartment && (
                    <h3 className="font-semibold text-lg mb-1">{booking.apartment.titre}</h3>
                  )}

                  <div className="flex flex-wrap gap-4 text-sm text-neutral-600 mt-2">
                    <span className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      {getClientName(booking)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {formatDate(booking.date_debut)} → {formatDate(booking.date_fin)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {booking.nb_nuits} nuit{booking.nb_nuits > 1 ? 's' : ''}
                    </span>
                  </div>
                  
                  <p className="text-xs text-neutral-400 mt-1">{getClientContact(booking)}</p>
                </div>
                
                <div className="flex flex-col items-end gap-2">
                  <div className="text-xl font-bold text-primary-600">
                    {formatPrice(booking.montant_total)} FCFA
                  </div>
                  
                  {/* Info acompte */}
                  {booking.is_deposit && (
                    <div className="text-right text-sm">
                      {booking.deposit_paid ? (
                        <div className="text-green-600">
                          <p>✓ Acompte: {formatPrice(booking.deposit_amount || 0)} FCFA</p>
                          <p className="text-orange-600">Reste: {formatPrice(booking.remaining_amount || 0)} FCFA</p>
                        </div>
                      ) : (
                        <div className="text-orange-600">
                          <p>Acompte à payer: {formatPrice(booking.deposit_amount || 0)} FCFA</p>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Actions */}
                  <div className="flex gap-2 flex-wrap">
                    {/* Bouton Modifier */}
                    {booking.status !== 'cancelled' && (
                      <button
                        onClick={() => openEditModal(booking)}
                        className="btn btn-sm btn-outline flex items-center gap-1"
                      >
                        <Edit3 className="w-4 h-4" />Modifier
                      </button>
                    )}
                    
                    {/* Bouton Valider - différent pour acompte ou paiement total */}
                    {booking.status === 'pending' && (
                      booking.is_deposit ? (
                        <button
                          onClick={() => handleConfirmDeposit(booking.id, booking.deposit_amount || 0)}
                          disabled={confirming === booking.id}
                          className="btn btn-sm bg-amber-500 text-white hover:bg-amber-600"
                        >
                          {confirming === booking.id ? (
                            <Loader className="w-4 h-4 animate-spin" />
                          ) : (
                            <><CheckCircle className="w-4 h-4 mr-1" />Valider acompte</>
                          )}
                        </button>
                      ) : (
                        <button
                          onClick={() => handleConfirmCash(booking.id)}
                          disabled={confirming === booking.id}
                          className="btn btn-sm bg-green-500 text-white hover:bg-green-600"
                        >
                          {confirming === booking.id ? (
                            <Loader className="w-4 h-4 animate-spin" />
                          ) : (
                            <><CheckCircle className="w-4 h-4 mr-1" />Valider</>
                          )}
                        </button>
                      )
                    )}
                    
                    {/* Bouton télécharger facture (si paiement validé - acompte ou complet) */}
                    {((booking.is_deposit && booking.deposit_paid) || 
                      (booking.status === 'confirmed' && booking.payment_status === 'completed')) && (
                      <button
                        onClick={() => handleDownloadInvoice(booking.id, booking.code_reservation)}
                        className="btn btn-sm btn-outline flex items-center gap-1"
                        title="Télécharger la facture"
                      >
                        <FileText className="w-4 h-4" />Facture
                      </button>
                    )}
                    
                    {/* Bouton Annuler la réservation */}
                    {booking.status !== 'cancelled' && booking.status !== 'completed' && (
                      <button
                        onClick={() => handleCancel(booking.id)}
                        disabled={confirming === booking.id}
                        className="btn btn-sm bg-red-500 text-white hover:bg-red-600 flex items-center gap-1"
                        title="Annuler la réservation"
                      >
                        <XCircle className="w-4 h-4" />
                        Annuler
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal de modification */}
      {showModal && editingBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-xl font-semibold">Modifier la réservation</h2>
              <button
                onClick={() => { setShowModal(false); setEditingBooking(null); }}
                className="p-2 hover:bg-neutral-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Code réservation */}
            <div className="px-4 pt-3 pb-0">
              <span className="text-sm text-neutral-500">Code: </span>
              <span className="font-mono font-medium text-primary-600">{editingBooking.code_reservation}</span>
            </div>
            
            <form onSubmit={handleSubmitEdit} className="p-4 space-y-4">
              {/* Appartement */}
              <div>
                <label className="block text-sm font-medium mb-1">Appartement</label>
                <select
                  value={editForm.apartment_id}
                  onChange={(e) => setEditForm({...editForm, apartment_id: Number(e.target.value)})}
                  className="input w-full"
                  required
                >
                  <option value="">Sélectionner un appartement</option>
                  {apartments.map(apt => (
                    <option key={apt.id} value={apt.id}>{apt.titre}</option>
                  ))}
                </select>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Date d'arrivée</label>
                  <input
                    type="date"
                    value={editForm.date_debut}
                    onChange={(e) => setEditForm({...editForm, date_debut: e.target.value})}
                    className="input w-full"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Date de départ</label>
                  <input
                    type="date"
                    value={editForm.date_fin}
                    onChange={(e) => setEditForm({...editForm, date_fin: e.target.value})}
                    className="input w-full"
                    required
                  />
                </div>
              </div>

              {/* Infos client */}
              <div className="border-t pt-4">
                <h3 className="font-medium mb-3 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Informations du client
                </h3>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">Nom complet *</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                      <input
                        type="text"
                        value={editForm.guest_name}
                        onChange={(e) => setEditForm({...editForm, guest_name: e.target.value})}
                        className="input w-full pl-10"
                        placeholder="Nom du client"
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Téléphone *</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                      <input
                        type="tel"
                        value={editForm.guest_phone}
                        onChange={(e) => setEditForm({...editForm, guest_phone: e.target.value})}
                        className="input w-full pl-10"
                        placeholder="+221 77 123 45 67"
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Email (optionnel)</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                      <input
                        type="email"
                        value={editForm.guest_email}
                        onChange={(e) => setEditForm({...editForm, guest_email: e.target.value})}
                        className="input w-full pl-10"
                        placeholder="email@exemple.com"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Nombre de personnes et statut */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Nombre de personnes</label>
                  <div className="relative">
                    <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={editForm.nb_personnes}
                      onChange={(e) => setEditForm({...editForm, nb_personnes: Number(e.target.value)})}
                      className="input w-full pl-10"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Statut</label>
                  <select
                    value={editForm.status}
                    onChange={(e) => setEditForm({...editForm, status: e.target.value as 'confirmed' | 'pending' | 'completed'})}
                    className="input w-full"
                  >
                    <option value="pending">En attente</option>
                    <option value="confirmed">Confirmée</option>
                    <option value="completed">Terminée</option>
                  </select>
                </div>
              </div>

              {/* Prix total */}
              <div>
                <label className="block text-sm font-medium mb-1">Prix total (FCFA)</label>
                <div className="relative">
                  <Banknote className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                  <input
                    type="number"
                    min="0"
                    step="100"
                    value={editForm.montant_total}
                    onChange={(e) => setEditForm({...editForm, montant_total: Number(e.target.value)})}
                    className="input w-full pl-10"
                    placeholder="Montant total"
                  />
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium mb-1">Notes (optionnel)</label>
                <div className="relative">
                  <FileText className="absolute left-3 top-3 w-4 h-4 text-neutral-400" />
                  <textarea
                    value={editForm.notes}
                    onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                    className="input w-full pl-10 min-h-[80px]"
                    placeholder="Notes sur la réservation..."
                  />
                </div>
              </div>

              {/* Aperçu */}
              {editForm.date_debut && editForm.date_fin && (
                <div className="p-3 rounded-lg bg-neutral-50 border">
                  <p className="text-sm text-neutral-600">
                    {new Date(editForm.date_debut).toLocaleDateString('fr-FR')} → {new Date(editForm.date_fin).toLocaleDateString('fr-FR')}
                    {' • '}
                    {Math.ceil((new Date(editForm.date_fin).getTime() - new Date(editForm.date_debut).getTime()) / (1000 * 60 * 60 * 24))} nuit(s)
                  </p>
                </div>
              )}

              {/* Boutons */}
              <div className="flex gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); setEditingBooking(null); }}
                  className="btn btn-outline flex-1"
                  disabled={submitting}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="btn btn-primary flex-1 flex items-center justify-center gap-2"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      Enregistrement...
                    </>
                  ) : (
                    <>
                      <Edit3 className="w-4 h-4" />
                      Enregistrer
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Bookings;
